<template>
  <div class="h-full flex flex-col bg-[#f8fafc] gap-6 overflow-auto">
    <CategoryModal
      ref="categoryModalRef"
      :isOpen="showModal"
      :editCategory="editingCategory"
      @close="closeModal"
      @save="createCategory"
      @update="updateCategory"
    />

    <!-- Table -->
    <div class="w-full overflow-x-auto overflow-y-auto max-h-96">
      <table class="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
        <thead>
          <tr>
            <th>No.</th>
            <th @click="sortBy('name')" class="sort-col">
              Name <span v-if="sortKey === 'name'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('remark')" class="sort-col">
              Remark <span v-if="sortKey === 'remark'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('created_at')" class="sort-col">
              Created At <span v-if="sortKey === 'created_at'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(cat, index) in sortedCategories" :key="cat.id" class="hover:bg-slate-100/50 transition-colors border-b">
            <td>{{ index + 1 }}</td>
            <td>{{ cat.name }}</td>
            <td class="max-w-[320px] truncate">{{ cat.remark || '-' }}</td>
            <td>{{ formatDate(cat.created_at) }}</td>
            <td>
              <div class="flex items-center gap-2 whitespace-nowrap">
                <button
                  type="button"
                  @click="editCategory(cat)"
                  class="editbtn text-emerald-600"
                  :class="canEditCategory ? 'hover:underline' : 'opacity-50 cursor-not-allowed action-disabled-marker'"
                  :disabled="!canEditCategory"
                >
                  Edit
                </button>

                <label
                  class="toggle-switch"
                  :class="[
                    cat.__togglingStatus ? 'opacity-50 pointer-events-none' : '',
                    !canEditCategory ? 'opacity-50 cursor-not-allowed action-disabled-marker' : ''
                  ]"
                  title="Toggle status"
                >
                  <input
                    type="checkbox"
                    :checked="(cat.status ?? 1) === 1"
                    :disabled="!canEditCategory"
                    @change="toggleCategoryStatus(cat)"
                  >
                  <span class="slider"></span>
                </label>
              </div>
            </td>
          </tr>
          <tr v-if="loading">
  <td colspan="9" class="text-center py-10">
    <div class="flex items-center justify-center">
      <div class="h-8 w-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
    </div>
  </td>
</tr>
          <tr v-else-if="error">
            <td colspan="5" class="text-center py-10 text-red-500">{{ error }}</td>
          </tr>
          <tr v-else-if="categories.length === 0">
            <td colspan="5" style="text-align:center; color:#94a3b8; padding: 48px 15px; font-style: italic;">
              No categories found for this company.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import store from '../../store'
import { apiFetch, WS_BASE_URL } from '../../utils/api'
import CategoryModal from './CategoryModal.vue'

const categories = ref([])
const loading = ref(false)
const error = ref('')
const sortKey = ref('name')
const sortOrder = ref('asc')

const showModal = ref(false)
const editingCategory = ref(null)
const categoryModalRef = ref(null)

let categorySyncSocket = null
let categorySyncReconnectTimer = null

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)
const userId = computed(() => currentUser.value?.id || null)
const userPrivileges = computed(() => {
  if (!currentUser.value?.privilege) return {}
  return typeof currentUser.value.privilege === 'string'
    ? JSON.parse(currentUser.value.privilege)
    : currentUser.value.privilege
})
const hasAdminPrivilege = computed(() => userPrivileges.value?.All === true || userPrivileges.value?.all === true)
const categoryPrivilege = computed(() =>
  userPrivileges.value?.['Stock Category'] ||
  userPrivileges.value?.['stock category'] ||
  null
)
const canAddCategory = computed(() => hasAdminPrivilege.value || !!categoryPrivilege.value?.Add)
const canEditCategory = computed(() => hasAdminPrivilege.value || !!categoryPrivilege.value?.Edit)

const sortedCategories = computed(() => {
  const result = [...categories.value]
  result.sort((a, b) => {
    const modifier = sortOrder.value === 'asc' ? 1 : -1
    const valA = (a[sortKey.value] ?? '').toString().toLowerCase()
    const valB = (b[sortKey.value] ?? '').toString().toLowerCase()
    if (valA < valB) return -1 * modifier
    if (valA > valB) return 1 * modifier
    return 0
  })
  return result
})

function sortBy(key) {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
}

function formatDate(value) {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toISOString().slice(0, 10)
}

async function loadCategories() {
  error.value = ''
  if (!companyId.value) {
    error.value = 'Company ID not found in user profile.'
    return
  }

  loading.value = true
  try {
    let endpoint = `/categories/company/${companyId.value}`
    if (branchId.value) {
      endpoint = `/categories/branch/${branchId.value}`
    }
    
    const response = await apiFetch(endpoint, { method: 'GET' })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.message || 'Failed to load categories')
    }
    const data = await response.json()
    categories.value = Array.isArray(data) ? data : []
  } catch (err) {
    error.value = err.message || 'Failed to load categories'
  } finally {
    loading.value = false
  }
}

function openAddModal() {
  if (!canAddCategory.value) return
  showModal.value = true
}

function closeModal() {
  showModal.value = false
  editingCategory.value = null
}

function editCategory(cat) {
  if (!canEditCategory.value) return
  editingCategory.value = cat
  showModal.value = true
}

async function createCategory(payload) {
  try {
    const response = await apiFetch('/categories', {
      method: 'POST',
      body: JSON.stringify({
        companyId: branchId.value ? null : companyId.value,
        branchId: branchId.value || null,
        name: payload.name,
        remark: payload.remark || null,
        created_by: userId.value,
      }),
    })

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      categoryModalRef.value?.setError(data.message || 'Failed to create category')
      return
    }

    await loadCategories()
    closeModal()
  } catch (err) {
    categoryModalRef.value?.setError(err.message || 'Failed to create category')
  }
}

async function updateCategory(payload) {
  try {
    const response = await apiFetch(`/categories/${payload.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        name: payload.name,
        remark: payload.remark || null,
        updated_by: userId.value,
      }),
    })

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      categoryModalRef.value?.setError(data.message || 'Failed to update category')
      return
    }

    await loadCategories()
    closeModal()
  } catch (err) {
    categoryModalRef.value?.setError(err.message || 'Failed to update category')
  }
}

async function toggleCategoryStatus(cat) {
  if (!canEditCategory.value) return
  if (!cat?.id || cat.__togglingStatus) return
  cat.__togglingStatus = true
  const current = (cat.status ?? 1) === 1 ? 1 : 0
  const next = current === 1 ? 0 : 1
  try {
    const res = await apiFetch(`/categories/${cat.id}/toggle-status`, {
      method: 'PATCH',
      body: JSON.stringify({ status: next })
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.message || 'Failed to toggle category status')
    }
    cat.status = next
  } catch (e) {
    alert(e.message || 'Failed to toggle category status')
  } finally {
    cat.__togglingStatus = false
  }
}

function onOpenCategoryModal() {
  if (!canAddCategory.value) return
  openAddModal()
}

function disconnectCategorySyncRealtime() {
  if (categorySyncReconnectTimer) {
    clearTimeout(categorySyncReconnectTimer)
    categorySyncReconnectTimer = null
  }
  if (categorySyncSocket) {
    try {
      categorySyncSocket.close()
    } catch (_) {
      // ignore close failures
    }
    categorySyncSocket = null
  }
}

function connectCategorySyncRealtime() {
  disconnectCategorySyncRealtime()

  const user = currentUser.value
  if (!user || typeof WebSocket === 'undefined') return

  const scopedCompanyId = user.companyId || user.company_id || user.company?.id
  if (!scopedCompanyId) return

  const scopedBranchId = user.branchId || user.branch_id || user.branch?.id || null
  const socket = new WebSocket(WS_BASE_URL)
  categorySyncSocket = socket

  socket.addEventListener('open', () => {
    socket.send(JSON.stringify({
      type: 'frontend.subscribe',
      company_id: scopedCompanyId,
      branch_id: scopedBranchId,
    }))
  })

  socket.addEventListener('message', (event) => {
    try {
      const message = JSON.parse(event.data)
      if (message.event?.startsWith('category.')) {
        loadCategories()
      }
    } catch (_) {
      // ignore malformed realtime payloads
    }
  })

  socket.addEventListener('close', () => {
    categorySyncSocket = null
    categorySyncReconnectTimer = setTimeout(() => {
      connectCategorySyncRealtime()
    }, 3000)
  })
}

onMounted(async () => {
  await loadCategories()
  connectCategorySyncRealtime()
  window.addEventListener('open-category-modal', onOpenCategoryModal)
})

onUnmounted(() => {
  disconnectCategorySyncRealtime()
  window.removeEventListener('open-category-modal', onOpenCategoryModal)
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.9rem;
  color: #1e293b;
}

th,
td {
  padding: 5px 6px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.8rem;
  white-space: nowrap;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
}

th.sort-col {
  cursor: pointer;
}
th.sort-col:hover {
  background: #e2e8f0;
}

tbody tr:hover {
  background: #f1f5f9;
}

.editbtn {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}

/* Toggle Switch (same style as departmentPage.vue / usersPage.vue) */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 28px;
  height: 15px;
  cursor: pointer;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 34px;
  transition: 0.2s;
}
.slider::before {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  left: 2px;
  top: 2.5px;
  background: white;
  border-radius: 50%;
  transition: 0.2s;
}
.toggle-switch input:checked + .slider {
  background: #22c55e;
}
.toggle-switch input:not(:checked) + .slider {
  background: #ef4444;
}
.toggle-switch input:checked + .slider::before {
  transform: translateX(13px);
}

.action-disabled-marker {
  position: relative;
}

.action-disabled-marker:hover::after {
  content: '⊘';
  position: absolute;
  top: -8px;
  right: -10px;
  font-size: 12px;
  font-weight: 700;
  color: #dc2626;
  line-height: 1;
  pointer-events: none;
}
</style>

